import {
  ADMIN_PERMISSION_ACCESS_LEVEL,
  ADMIN_PERMISSION_RESOURCE_TYPE,
  type AdminMembersAccessApiItem,
  type AdminMembersAccessItem,
  type AdminMembersAccessPermission,
  type AdminPermission,
} from '@/types';
import type { ComboboxItemExtended } from '@/types/compoboxItemExtended.type';
import type { GroupAdminMemberLookup } from '@/types/member.type';
import { isCurrentUserChurchAdmin } from './user';

export const CHURCH_ADMIN_MANAGEABLE_RESOURCE_TYPES: ADMIN_PERMISSION_RESOURCE_TYPE[] = [
  ADMIN_PERMISSION_RESOURCE_TYPE.APP_SETUP,
  ADMIN_PERMISSION_RESOURCE_TYPE.PARISH_INFO,
  ADMIN_PERMISSION_RESOURCE_TYPE.MEMBERS,
  ADMIN_PERMISSION_RESOURCE_TYPE.TEXT_CREDIT,
];

export const DEFAULT_CHURCH_ADMIN_PERMISSIONS: AdminPermission[] = CHURCH_ADMIN_MANAGEABLE_RESOURCE_TYPES.map(
  (resourceType) => ({
    resourceType,
    accessLevel: ADMIN_PERMISSION_ACCESS_LEVEL.EDIT,
  })
);

export const getPermissionAccessLevel = ({
  permissions,
  resourceType,
}: {
  permissions: AdminPermission[] | undefined;
  resourceType: ADMIN_PERMISSION_RESOURCE_TYPE;
}): ADMIN_PERMISSION_ACCESS_LEVEL | undefined => {
  if (!permissions?.length) {
    return undefined;
  }

  return permissions.find((permission) => permission.resourceType === resourceType)?.accessLevel;
};

export const hasViewAccess = ({
  permissions,
  resourceType,
}: {
  permissions: AdminPermission[] | undefined;
  resourceType: ADMIN_PERMISSION_RESOURCE_TYPE;
}): boolean => {
  const accessLevel = getPermissionAccessLevel({ permissions, resourceType });

  if (!accessLevel) {
    return true;
  }

  return accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT || accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY;
};

export const hasEditAccess = ({
  permissions,
  resourceType,
}: {
  permissions: AdminPermission[] | undefined;
  resourceType: ADMIN_PERMISSION_RESOURCE_TYPE;
}): boolean => {
  const accessLevel = getPermissionAccessLevel({ permissions, resourceType });

  if (!accessLevel) {
    return true;
  }

  return accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
};

export const isAccessDenied = ({
  permissions,
  resourceType,
}: {
  permissions: AdminPermission[] | undefined;
  resourceType: ADMIN_PERMISSION_RESOURCE_TYPE;
}): boolean => {
  return getPermissionAccessLevel({ permissions, resourceType }) === ADMIN_PERMISSION_ACCESS_LEVEL.DENY;
};

export const normalizeChurchAdminPermissions = (permissions: AdminPermission[] | undefined): AdminPermission[] => {
  if (!permissions?.length) {
    return [...DEFAULT_CHURCH_ADMIN_PERMISSIONS];
  }

  return CHURCH_ADMIN_MANAGEABLE_RESOURCE_TYPES.map((resourceType) => {
    const existingPermission = permissions.find((permission) => permission.resourceType === resourceType);

    if (!existingPermission || existingPermission.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.DENY) {
      return {
        resourceType,
        accessLevel: ADMIN_PERMISSION_ACCESS_LEVEL.EDIT,
      };
    }

    return existingPermission;
  });
};

export type MembersAccessLevel = ADMIN_PERMISSION_ACCESS_LEVEL.EDIT | ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY;

export const DEFAULT_MEMBERS_ACCESS_LEVEL: MembersAccessLevel = ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;

export const resolveMembersAccessLevel = ({
  accessLevel,
  permissions,
}: {
  accessLevel?: ADMIN_PERMISSION_ACCESS_LEVEL | string;
  permissions?: AdminMembersAccessPermission[];
}): MembersAccessLevel => {
  if (accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY) {
    return ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY;
  }

  if (accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT) {
    return ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
  }

  if (!permissions?.length) {
    return DEFAULT_MEMBERS_ACCESS_LEVEL;
  }

  const membersPermission =
    permissions.find((permission) => permission.resourceType === ADMIN_PERMISSION_RESOURCE_TYPE.MEMBERS) ??
    permissions.find(
      (permission) =>
        permission.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY ||
        permission.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT
    );

  return membersPermission?.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY
    ? ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY
    : ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
};

const findAdminMembersAccessItem = ({
  items,
  memberId,
  userId,
}: {
  items?: AdminMembersAccessApiItem[];
  memberId: string;
  userId?: string;
}): AdminMembersAccessApiItem | undefined => {
  if (!items?.length) {
    return undefined;
  }

  return items.find(
    (item) =>
      item.id === memberId ||
      item.id === userId ||
      item.userId === memberId ||
      (userId ? item.userId === userId : false)
  );
};

const resolveAdminMembersAccessLevel = (item?: AdminMembersAccessApiItem): MembersAccessLevel =>
  resolveMembersAccessLevel({
    accessLevel: item?.accessLevel,
    permissions: item?.permissions,
  });

const isMembersEditAccess = ({
  accessLevel,
  permissions,
}: {
  accessLevel?: ADMIN_PERMISSION_ACCESS_LEVEL | string;
  permissions?: AdminMembersAccessPermission[];
}): boolean => {
  if (accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT || accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.VIEW_ONLY) {
    return accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
  }

  const membersPermission = permissions?.find(
    (permission) => permission.resourceType === ADMIN_PERMISSION_RESOURCE_TYPE.MEMBERS
  );

  return membersPermission?.accessLevel === ADMIN_PERMISSION_ACCESS_LEVEL.EDIT;
};

export const getGroupSettingsAdminMembersAccess = (
  groupSettings:
    | {
        adminMembersAccess?: AdminMembersAccessApiItem[];
        adminMemberAccess?: AdminMembersAccessApiItem[];
      }
    | null
    | undefined
): AdminMembersAccessApiItem[] | undefined => {
  if (groupSettings?.adminMembersAccess?.length) {
    return groupSettings.adminMembersAccess;
  }

  return groupSettings?.adminMemberAccess ?? groupSettings?.adminMembersAccess;
};

/**
 * Group admins are limited to the access level assigned on this group.
 * Missing assignment is view only. Church admins are handled by the caller.
 */
export const resolveCurrentUserGroupEditAccess = ({
  currentUserId,
  currentUserEmail,
  admins,
  adminMembersAccess,
}: {
  currentUserId?: string;
  currentUserEmail?: string;
  admins?: GroupAdminMemberLookup[];
  adminMembersAccess?: AdminMembersAccessApiItem[];
}): boolean => {
  const matchedAdmin = admins?.find((admin) =>
    isCurrentUserChurchAdmin({
      admin,
      currentUserId,
      currentUserEmail,
    })
  );

  const accessItem = findAdminMembersAccessItem({
    items: adminMembersAccess,
    memberId: matchedAdmin?.id ?? currentUserId ?? '',
    userId: matchedAdmin?.userId ?? currentUserId,
  });

  return isMembersEditAccess({
    accessLevel: accessItem?.accessLevel,
    permissions: accessItem?.permissions ?? matchedAdmin?.permissions,
  });
};

export const getGroupAdminRequestId = ({ admin }: { admin: ComboboxItemExtended<GroupAdminMemberLookup> }): string =>
  admin.value;

export const normalizeAdminMembersAccessFromGroupSettings = ({
  admins,
  adminMembersAccess,
}: {
  admins: ComboboxItemExtended<GroupAdminMemberLookup>[];
  adminMembersAccess?: AdminMembersAccessApiItem[];
}): AdminMembersAccessItem[] =>
  admins.map((admin) => {
    const memberId = admin.fullData?.id ?? admin.value;
    const userId = admin.fullData?.userId;
    const accessItem = findAdminMembersAccessItem({
      items: adminMembersAccess,
      memberId,
      userId,
    });

    return {
      id: getGroupAdminRequestId({ admin }),
      accessLevel: resolveMembersAccessLevel({
        accessLevel: accessItem?.accessLevel,
        permissions: accessItem?.permissions ?? admin.fullData?.permissions,
      }),
    };
  });

export const buildAdminMembersAccess = ({
  admins,
  existingAccess,
}: {
  admins: ComboboxItemExtended<GroupAdminMemberLookup>[];
  existingAccess?: AdminMembersAccessApiItem[];
}): AdminMembersAccessItem[] => {
  return admins.map((admin) => {
    const memberId = admin.fullData?.id ?? admin.value;
    const userId = admin.fullData?.userId;
    const existingItem = findAdminMembersAccessItem({
      items: existingAccess,
      memberId,
      userId,
    });

    return {
      id: getGroupAdminRequestId({ admin }),
      accessLevel: resolveAdminMembersAccessLevel(existingItem),
    };
  });
};

export const NAV_ITEM_PERMISSION_RESOURCE: Partial<Record<string, ADMIN_PERMISSION_RESOURCE_TYPE>> = {
  members: ADMIN_PERMISSION_RESOURCE_TYPE.MEMBERS,
  'parish-info': ADMIN_PERMISSION_RESOURCE_TYPE.PARISH_INFO,
  'app-setup': ADMIN_PERMISSION_RESOURCE_TYPE.APP_SETUP,
  textStatus: ADMIN_PERMISSION_RESOURCE_TYPE.TEXT_CREDIT,
};

export const isNavItemVisibleForPermissions = ({
  navItemKey,
  permissions,
}: {
  navItemKey: string;
  permissions: AdminPermission[] | undefined;
}): boolean => {
  const resourceType = NAV_ITEM_PERMISSION_RESOURCE[navItemKey];

  if (!resourceType) {
    return true;
  }

  return hasViewAccess({ permissions, resourceType });
};

export const mergeAdminMembersAccess = ({
  admins,
  accessItems,
}: {
  admins: ComboboxItemExtended<GroupAdminMemberLookup>[];
  accessItems: AdminMembersAccessItem[];
}): AdminMembersAccessItem[] => {
  return buildAdminMembersAccess({ admins, existingAccess: accessItems });
};
